/**
 * The narrated steps shown while the ATS report is generating, and the pure
 * timing logic behind them.
 *
 * Deliberately free of Solid imports: this is the part worth unit-testing, and
 * keeping it framework-free lets the test run in node instead of jsdom.
 *
 * **Why the progress is simulated.** `POST /v1/ats/job-offers/:id/report` is a
 * single blocking request that returns the PDF — there are no progress events
 * to subscribe to. So the phases advance on a timer, and the *last* one holds
 * until the request actually resolves. No phase ever claims a step finished
 * because a clock said so.
 *
 * The bar follows the same discipline: it runs on the estimate up to
 * {@link PROGRESS_CEILING} and only completes when the PDF is actually in
 * hand. Nothing here counts documents.
 */

import { REPORT_APPLICANT_CAP } from "../constants";

export type ReportPhase = {
  /** i18n key suffix under `profile.company.ats.report.loader.phases`. */
  key: string;
  /**
   * Share of the scripted narration this phase occupies. Relative, not
   * absolute: the total is stretched to fit the expected generation time.
   * The terminal phase has no weight — it waits on the network instead.
   */
  weight: number;
  /** True for the single phase that holds until the PDF arrives. */
  terminal: boolean;
};

/**
 * Measured on the real endpoint (2026-09-13): 19.9s for 19 applicants with a
 * cold evaluation cache, i.e. ~1.05s each at the backend's concurrency of 3.
 */
const MS_PER_APPLICANT = 1050;

/**
 * Floor for the narration when the applicant count is unknown or tiny. Without
 * it a 1-applicant offer would race through every phase in a second, which
 * reads as a stutter rather than a sequence.
 */
const MIN_NARRATION_MS = 6_000;

/**
 * How long the dialog stays up even when the PDF is already in hand. The warm
 * path answers in ~180ms; without a floor the dialog would appear and vanish
 * inside a frame or two and read as a glitch, not as a completed operation.
 * 800ms covers one sheet-flight cycle plus time to read the first phase.
 */
export const MIN_DIALOG_VISIBLE_MS = 800;

/**
 * The steps, in order. Copy lives in i18n; the constraint on that copy is that
 * every string stays short enough not to wrap (the dialog reserves a fixed
 * height) and stays phrased as work *in progress* — never as a finished
 * result, or a degraded report would contradict what the loader just said.
 */
export const REPORT_PHASES: ReportPhase[] = [
  { key: "reading", weight: 1, terminal: false },
  { key: "skills", weight: 1.2, terminal: false },
  { key: "matching", weight: 1.4, terminal: false },
  { key: "evaluating", weight: 1.4, terminal: false },
  { key: "composing", weight: 0, terminal: true },
];

const TOTAL_WEIGHT = REPORT_PHASES.reduce(
  (sum, phase) => sum + phase.weight,
  0,
);

/**
 * How long the scripted part of the narration should last for an offer of this
 * size. The estimate does not need to be exact — the terminal phase absorbs an
 * underestimate by holding, and an overestimate ends early when the PDF lands.
 */
export const narrationDurationMs = (applicantCount: number | undefined) => {
  // Cappato al limite del report: il backend non ne costruisce mai piu' di
  // REPORT_APPLICANT_CAP, quindi un'offerta con 300 candidature narrava cinque
  // minuti per un lavoro da ~50 secondi. L'attesa era inventata.
  const count = Math.min(applicantCount ?? 0, REPORT_APPLICANT_CAP);
  return Math.max(count * MS_PER_APPLICANT, MIN_NARRATION_MS);
};

/**
 * Index of the phase to show after `elapsedMs`. Monotonic by construction, and
 * saturates on the terminal phase: once the script runs out, the loader waits
 * there for as long as the request needs.
 */
export const phaseAt = (
  elapsedMs: number,
  applicantCount: number | undefined,
) => {
  const terminalIndex = REPORT_PHASES.length - 1;
  if (elapsedMs <= 0) return 0;

  const total = narrationDurationMs(applicantCount);
  let consumed = 0;

  for (let i = 0; i < terminalIndex; i++) {
    consumed += (REPORT_PHASES[i].weight / TOTAL_WEIGHT) * total;
    if (elapsedMs < consumed) return i;
  }

  return terminalIndex;
};

/**
 * Where the estimated run stops while the request is still in flight.
 *
 * The bar never reaches 100% on a clock: the estimate cannot know when the PDF
 * will actually land, and a bar sitting at full while the user waits is the
 * same lie the old per-candidate counter told. The last stretch is not time —
 * it is the response arriving.
 */
export const PROGRESS_CEILING = 0.9;

/**
 * How long the bar takes to close the final stretch, and how long the full bar
 * stays on screen afterwards.
 *
 * The hold is not decoration. Without it the fill and the dialog's dismissal
 * land in the same frame, and the user sees the bar vanish *while* it fills
 * instead of completing. 420ms is enough to read the full bar as "done".
 */
export const PROGRESS_FILL_MS = 260;
export const PROGRESS_HOLD_AT_FULL_MS = 420;

/**
 * Progress of the loader bar, 0→1.
 *
 * Two inputs only, and they are the only two the UI genuinely has: time spent,
 * and whether the PDF is in hand. Nothing counts documents — the backend
 * reports no per-candidate progress, so any such number was invented.
 *
 * The run decelerates (1 - (1-t)²) rather than climbing linearly, so an
 * overlong estimate does not leave the bar stranded mid-track, and the approach
 * to the ceiling reads as "nearly there" instead of as a stall.
 */
export const progressAt = (
  elapsedMs: number,
  applicantCount: number | undefined,
  arrived: boolean,
) => {
  if (arrived) return 1;
  if (elapsedMs <= 0) return 0;

  const t = Math.min(elapsedMs / narrationDurationMs(applicantCount), 1);
  return PROGRESS_CEILING * (1 - (1 - t) ** 2);
};
